import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlignLeft,
  ChartSpline,
  Info,
  ShoppingBag,
  Smartphone,
  Waypoints,
} from "lucide-react";
import { FC, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router";

import { AnalyticsTab } from "@/components/sku/analytics-tab";
import { BlockchainTab } from "@/components/sku/blockchain-tab";
import { OverviewTab } from "@/components/sku/overview-tab";
import { ProductTab } from "@/components/sku/product-tab";
import { WebExperienceTab } from "@/components/sku/webpage-tab";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Form } from "@/components/ui/form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useGetSKUQuery } from "@/data/queries";
import { SessionRoleLabels, SKU } from "@/data/types";
import { DEFAULT_NETWORK } from "@/lib/chains";
import { skuFormSchema } from "@/lib/forms";
import { canEditSKU } from "@/lib/permissions";
import useStore from "@/state";

type Props = {
  sku: SKU;
};

const SkuMain: FC<Props> = ({ sku }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { skuId } = useParams<{ skuId: string }>();
  const { session } = useStore();

  const getCurrentTab = () => {
    const pathParts = location.pathname.split("/");
    const current = pathParts[pathParts.length - 1];

    if (current === skuId || current === "") {
      return "overview";
    }

    return [
      "overview",
      "analytics",
      "webpage",
      "blockchain",
      "product",
    ].includes(current)
      ? current
      : "overview";
  };

  const activeTab = getCurrentTab();

  const form = useForm({
    resolver: zodResolver(skuFormSchema),
    defaultValues: {
      name: sku.name,
      notes: sku.notes ?? undefined,
      description: sku.description ?? undefined,
      analytics_url: sku.analytics_url ?? undefined,
      image_url: sku.image_url ?? undefined,
      animation_url: sku.animation_url ?? undefined,
      header_logo_url: sku.header_logo_url ?? undefined,
      builder_io_url: sku.builder_io_url ?? undefined,
      builder_io_api_key: sku.builder_io_api_key ?? undefined,
      login_type: sku.login_type,
      redirect_url: sku.redirect_url ?? undefined,
      nft_chain_id: sku.nft_chain_id
        ? String(sku.nft_chain_id)
        : String(DEFAULT_NETWORK),
      page_type: sku.page_type,
      nft_token_id: 1,
      total_tags_count: sku.tags_count,
    },
  });

  const handleTabChange = (value: string) => {
    if (value === "overview") {
      navigate(`/skus/${skuId}`);
    } else {
      navigate(`/skus/${skuId}/${value}`);
    }
  };

  const lacksPermission = useMemo(() => {
    return !canEditSKU(session?.permissions);
  }, [session]);

  return (
    <Form {...form}>
      <div className="flex -my-8">
        <div className="py-8 flex-1">
          <h2 className="scroll-m-20 text-3xl font-medium tracking-tight first:mt-0">
            SKU {sku.id} - {sku.name}
          </h2>
          <p className="mt-3 text-muted-foreground">
            The SKU ID number is used for the programming and managing
            Legitimate Tags. This is set by Legitimate and cannot be changed.
          </p>

          <Tabs
            defaultValue="overview"
            className="mt-6"
            onValueChange={handleTabChange}
            value={activeTab}
          >
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="overview">
                <AlignLeft /> Overview
              </TabsTrigger>
              <TabsTrigger value="product">
                <ShoppingBag /> Product
              </TabsTrigger>
              <TabsTrigger value="analytics">
                <ChartSpline /> Analytics
              </TabsTrigger>
              <TabsTrigger value="webpage">
                <Smartphone /> Webpage
              </TabsTrigger>
              <TabsTrigger value="blockchain">
                <Waypoints /> Blockchain
              </TabsTrigger>
            </TabsList>
            {lacksPermission && (
              <Alert className="mt-6 bg-muted">
                <Info className="h-4 w-4" />
                <AlertTitle className="text-base">
                  {session?.role
                    ? SessionRoleLabels[session?.role]
                    : session?.role}{" "}
                  Role
                </AlertTitle>
                <AlertDescription className="text-sm">
                  Due to your current permissions, all information on this
                  screen is read-only. Contact Legitimate or your account admin
                  to update your role.
                </AlertDescription>
              </Alert>
            )}
            <TabsContent value="overview">
              <OverviewTab sku={sku} />
            </TabsContent>
            <TabsContent value="product">
              <ProductTab sku={sku} />
            </TabsContent>
            <TabsContent value="analytics">
              <AnalyticsTab sku={sku} />
            </TabsContent>
            <TabsContent value="webpage">
              <WebExperienceTab sku={sku} />
            </TabsContent>
            <TabsContent value="blockchain">
              <BlockchainTab sku={sku} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Form>
  );
};

export const Sku = () => {
  const { skuId } = useParams<{ skuId: string }>();
  const { data, isLoading, isError, error } = useGetSKUQuery(skuId!);

  if (isLoading) return <div />;

  if (isError) return <div>Error: {error.message}</div>;

  if (!data) {
    return <div>No data</div>;
  }

  return <SkuMain sku={data} />;
};
